/**
 * LIENZO DE VIDEOVIGILANCIA — el plano donde se colocan y se APUNTAN las cámaras.
 * =============================================================================
 *
 * Hermano de `Jardin.jsx` y con la misma gramática táctil: se toca para poner, se arrastra
 * para mover, los lados dicen cuánto miden. Lo que cambia es lo esencial del rubro:
 *
 *   **Un aspersor no se apunta. Una cámara sí.**
 *
 * Un aspersor de 360° da igual cómo caiga. Una cámara mal orientada cubre el muro de
 * enfrente en vez de la puerta, y en el plano se ve igual de bonita. Por eso aquí hay dos
 * cosas que arrastrar por cámara: el **cuerpo**, que la mueve, y el **puño del eje**, que
 * la gira.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTE DIBUJO TIENE QUE VENDER
 *
 * El argumento de venta del rubro no es «te pongo cuatro cámaras». Es **«desde aquí
 * identificas, desde aquí sólo reconoces, y de aquí para allá nada más detectas»**. Por eso
 * el sector no se pinta de un solo color: se pintan las capas de cobertura anidadas, del
 * más exigente al más laxo, y se ven como una diana.
 *
 * Es la misma idea que hizo funcionar el plano de riego —mostrar el hueco seco en vez de
 * un número— aplicada a lo que aquí es el hueco: la zona donde no vas a reconocer a nadie.
 */

import { useRef, useState, useMemo } from 'react'
import { caja, dentro, lados } from '../motor/geometria.js'
import { ADAPTADOR_CAMARAS } from './adaptadores.js'

/** Punto de la circunferencia a un ángulo dado. Grados, y `y` crece hacia abajo en SVG. */
const enAngulo = (cx, cy, r, grados) => {
  const a = (grados * Math.PI) / 180
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }
}

/** Camino SVG de un sector circular centrado en un rumbo. */
function sectorPath (cx, cy, radio, rumbo, apertura) {
  if (apertura >= 360) {
    return `M ${cx - radio} ${cy} a ${radio} ${radio} 0 1 0 ${radio * 2} 0 a ${radio} ${radio} 0 1 0 ${-radio * 2} 0`
  }
  const ini = enAngulo(cx, cy, radio, rumbo - apertura / 2)
  const fin = enAngulo(cx, cy, radio, rumbo + apertura / 2)
  const grande = apertura > 180 ? 1 : 0
  return `M ${cx} ${cy} L ${ini.x} ${ini.y} A ${radio} ${radio} 0 ${grande} 1 ${fin.x} ${fin.y} Z`
}

export default function Recinto ({
  poligono, setPoligono,
  camaras, setCamaras,           // los equipos colocados, sea cual sea el rubro
  rack, setRack,
  cerrado, setCerrado,
  modo,                  // 'rack' | 'trazo' | 'colocar' | 'ver'
  modeloActivo,
  nivelObjetivo = 'reconocimiento',   // qué capa se mide en la lectura de abajo
  seleccion, setSeleccion,
  /* Lo que distingue un rubro de otro al dibujar cobertura. Ver `adaptadores.js`.
     Por defecto, videovigilancia: es para lo que se escribió este lienzo y es lo que
     está verificado en el navegador. */
  adaptador = ADAPTADOR_CAMARAS,
  etiquetaRack = 'Rack'
}) {
  const svgRef = useRef(null)
  const [arrastrando, setArrastrando] = useState(null)
  /* Misma bandera que en `Jardin.jsx`, y por la misma razón medida allá: soltar un
     arrastre dispara `click` justo después, y sin esto colocaba otra cámara encima. */
  const hubieArrastre = useRef(false)

  const vista = useMemo(() => {
    const pts = [...poligono, ...camaras, ...(rack ? [rack] : [])]
    if (pts.length < 2) return { x: -2, y: -2, w: 22, h: 16 }
    const b = caja(pts)
    const m = 2.5
    return { x: b.x0 - m, y: b.y0 - m, w: (b.x1 - b.x0) + m * 2, h: (b.y1 - b.y0) + m * 2 }
  }, [poligono, camaras, rack])

  const u = vista.w / 100
  const tipo = u * 3.4

  const enMetros = ev => {
    const svg = svgRef.current
    const p = svg.createSVGPoint()
    const t = ev.touches?.[0] || ev.changedTouches?.[0] || ev
    p.x = t.clientX; p.y = t.clientY
    const m = p.matrixTransform(svg.getScreenCTM().inverse())
    return { x: Math.round(m.x * 2) / 2, y: Math.round(m.y * 2) / 2 }
  }

  /** Centro del recinto. Sirve para apuntar una cámara nueva a algo razonable. */
  const centro = useMemo(() => {
    if (poligono.length < 3) return null
    const sx = poligono.reduce((s, p) => s + p.x, 0) / poligono.length
    const sy = poligono.reduce((s, p) => s + p.y, 0) / poligono.length
    return { x: sx, y: sy }
  }, [poligono])

  const tocar = ev => {
    if (hubieArrastre.current) { hubieArrastre.current = false; return }
    const p = enMetros(ev)
    if (modo === 'rack') { setRack(p); return }
    if (modo === 'trazo') {
      if (cerrado) return
      setPoligono([...poligono, p])
      return
    }
    if (modo === 'colocar') {
      /* Los equipos SÍ se colocan fuera del polígono, al revés que los aspersores: una
         cámara normalmente va en la barda o la fachada apuntando hacia adentro, y un punto
         de acceso puede ir en el pasillo que da al jardín. */
      /* Se apunta solo hacia el centro del recinto. Es la orientación correcta la mayoría
         de las veces, y sobre todo evita que un equipo recién puesto aparezca mirando al
         este por casualidad y parezca que no cubre nada. En un rubro no apuntable —Wi-Fi—
         el rumbo se guarda igual pero no significa nada: el sector es de 360°. */
      const rumbo = centro
        ? (Math.atan2(centro.y - p.y, centro.x - p.x) * 180) / Math.PI
        : 0
      const id = Math.random().toString(36).slice(2, 8)
      setCamaras([...camaras, { id, x: p.x, y: p.y, modelo: modeloActivo, rumbo: Math.round(rumbo) }])
      setSeleccion?.(id)
    }
  }

  const tomar = (ev, objetivo) => {
    ev.stopPropagation()
    setArrastrando(objetivo)
    if (objetivo.tipo === 'camara' || objetivo.tipo === 'mira') setSeleccion?.(objetivo.id)
  }

  const mover = ev => {
    if (!arrastrando) return
    ev.preventDefault()
    hubieArrastre.current = true
    const p = enMetros(ev)
    if (arrastrando.tipo === 'camara') {
      setCamaras(camaras.map(c => c.id === arrastrando.id ? { ...c, x: p.x, y: p.y } : c))
    } else if (arrastrando.tipo === 'mira') {
      /* Girar: el rumbo es el ángulo del dedo respecto al cuerpo de la cámara. No se
         redondea a la media rejilla —eso es para posiciones— sino a grados enteros. */
      setCamaras(camaras.map(c => {
        if (c.id !== arrastrando.id) return c
        const g = (Math.atan2(p.y - c.y, p.x - c.x) * 180) / Math.PI
        return { ...c, rumbo: Math.round(g) }
      }))
    } else if (arrastrando.tipo === 'vertice') {
      setPoligono(poligono.map((v, i) => i === arrastrando.i ? p : v))
    } else if (arrastrando.tipo === 'rack') {
      setRack(p)
    }
  }

  const soltar = () => setArrastrando(null)

  const capas = useMemo(() => adaptador.capas(), [adaptador])

  const cob = useMemo(
    () => cerrado && camaras.length ? adaptador.cobertura(poligono, camaras, nivelObjetivo) : null,
    [poligono, camaras, cerrado, nivelObjetivo, adaptador]
  )

  const misLados = lados(poligono, cerrado)

  return (
    <div className="lienzo-plano">
      <svg ref={svgRef}
        viewBox={`${vista.x} ${vista.y} ${vista.w} ${vista.h}`}
        onClick={tocar}
        onMouseMove={mover} onMouseUp={soltar} onMouseLeave={soltar}
        onTouchMove={mover} onTouchEnd={soltar}
        role="img" aria-label="Plano del recinto con las cámaras colocadas">

        <defs>
          {/* La máscara recorta la cobertura al recinto: pintar el cono completo saliéndose
              por encima de la barda hace ver cubierto lo que está en la calle del vecino. */}
          <mask id="dentro-recinto">
            <rect x={vista.x} y={vista.y} width={vista.w} height={vista.h} fill="black" />
            {cerrado && poligono.length > 2 && (
              <polygon points={poligono.map(p => `${p.x},${p.y}`).join(' ')} fill="white" />
            )}
          </mask>
        </defs>

        {/* ---- El recinto ---- */}
        {poligono.length > 1 && (
          <polygon
            points={poligono.map(p => `${p.x},${p.y}`).join(' ')}
            className={cerrado ? 'terreno-borde' : 'terreno-borde abierto'}
            strokeWidth={u * 0.55} />
        )}

        {/* ---- Cobertura, capa por capa ----
            En videovigilancia son cuatro capas anidadas del alcance más largo al más corto,
            y se leen como una diana: la misma pintura con más opacidad hace obvio, sin leer
            un número, que identificar cuesta cerca y detectar alcanza lejos.

            En Wi-Fi es una sola capa, porque un dispositivo se conecta a UNA banda a la vez
            y pintar las tres anidadas daría la impresión de que 5 GHz llega donde llega
            2.4. El adaptador decide cuántas capas hay y con qué radio. */}
        <g mask="url(#dentro-recinto)">
          {capas.map(capa => (
            <g key={capa.id}>
              {camaras.map(c => (
                <path key={c.id}
                  d={sectorPath(c.x, c.y, adaptador.radio(c, capa.id), c.rumbo || 0, adaptador.apertura(c))}
                  className="vision" opacity={capa.opacidad} />
              ))}
            </g>
          ))}
        </g>

        {/* ---- Lados con su medida ---- */}
        {/* `lados()` devuelve `largo` y `medio` ya calculados — no `metros`, que es como
            lo llamé primero y reventó la pantalla en el primer trazo. */}
        {misLados.map(l => (
          <g key={l.i}>
            <line x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y}
              className="borde" strokeWidth={u * 0.35} />
            <text x={l.medio.x} y={l.medio.y - u * 1.2}
              className="cota-txt" fontSize={tipo} textAnchor="middle">
              {l.largo.toFixed(1)} m
            </text>
          </g>
        ))}

        {/* ---- Vértices ---- */}
        {poligono.map((v, i) => (
          <circle key={i} cx={v.x} cy={v.y} r={u * 1.5} className="vertice"
            onMouseDown={e => tomar(e, { tipo: 'vertice', i })}
            onTouchStart={e => tomar(e, { tipo: 'vertice', i })} />
        ))}

        {/* ---- El rack, de donde sale todo el cable ---- */}
        {rack && (
          <g onMouseDown={e => tomar(e, { tipo: 'rack' })}
             onTouchStart={e => tomar(e, { tipo: 'rack' })}>
            <rect x={rack.x - u * 1.8} y={rack.y - u * 1.8} width={u * 3.6} height={u * 3.6}
              className="rack" rx={u * 0.5} />
            <text x={rack.x} y={rack.y + u * 5} className="cota-txt" fontSize={tipo}
              textAnchor="middle">{etiquetaRack}</text>
          </g>
        )}

        {/* ---- Los equipos ---- */}
        {camaras.map(c => {
          /* El puño de giro va sobre el eje, a una distancia fija en unidades de pantalla
             —no en metros— para que sea igual de agarrable con cualquier zoom.
             Sólo existe si el rubro es apuntable: un punto de acceso Wi-Fi no se orienta,
             y ofrecer un tirador que no cambia nada es peor que no ofrecerlo. */
          const puno = enAngulo(c.x, c.y, u * 9, c.rumbo || 0)
          const activa = seleccion === c.id
          return (
            <g key={c.id} className={activa ? 'camara activa' : 'camara'}>
              {adaptador.apuntable && (
                <>
                  <line x1={c.x} y1={c.y} x2={puno.x} y2={puno.y}
                    className="eje" strokeWidth={u * 0.4} />
                  <circle cx={puno.x} cy={puno.y} r={u * 2.2} className="mira"
                    onMouseDown={e => tomar(e, { tipo: 'mira', id: c.id })}
                    onTouchStart={e => tomar(e, { tipo: 'mira', id: c.id })} />
                </>
              )}
              <circle cx={c.x} cy={c.y} r={u * 2.4} className="cuerpo"
                onMouseDown={e => tomar(e, { tipo: 'camara', id: c.id })}
                onTouchStart={e => tomar(e, { tipo: 'camara', id: c.id })} />
              {/* La etiqueta sólo se pinta en el equipo SELECCIONADO. Rotularlos todos
                  llenaba el plano de etiquetas que chocan entre sí y con la del rack —se
                  vio en la primera captura—, y el dato ya está en el panel de al lado.
                  Un plano que se le enseña a un cliente compite por atención: cada
                  etiqueta que no está decidiendo algo, estorba. */}
              {activa && (
                <text x={c.x} y={c.y - u * 4.5} className="cota-txt" fontSize={tipo}
                  textAnchor="middle">{adaptador.etiqueta(c)}</text>
              )}
            </g>
          )
        })}
      </svg>

      {/* La lectura del plano en una línea. El porcentaje va SIEMPRE acompañado de contra
          qué se midió: «cobertura» a secas no quiere decir nada, y en los dos rubros que
          usan este lienzo significa cosas distintas —un nivel de detalle en cámaras, una
          banda y un umbral en Wi-Fi—. */}
      {cob && (
        <div className="cota-jardin">
          <b>{cob.porcentaje.toFixed(0)}%</b> del recinto queda cubierto en{' '}
          {adaptador.nombreCapa(nivelObjetivo)}
          {cob.estimado && <span className="s"> · estimado, se valida midiendo</span>}
        </div>
      )}
    </div>
  )
}
